import React, { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Amount from "../../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import MetricSummaryTile from "../../../ui-components/metric-summary-tile/metricSummaryTile";
import Table from "../../../ui-components/table/table";
import { PaymentAggregates } from "../../../app/data/payment/PaymentSession";
import PaymentSummarySection from "./paymentSummarySection";
import { formatPct, PAYMENT_PROVIDER_OPTIONS } from "./paymentAdminUtils";

type ProviderRow = PaymentAggregates["completedAmountByProvider"][number];

const providerColumnHelper = createColumnHelper<ProviderRow>();

export interface PaymentReportSectionProps {
  reportAccount: string;
  reportProvider: string;
  reportFrom: string;
  reportTo: string;
  aggregates: PaymentAggregates | null;
  reportError: string | null;
  isLoading: boolean;
  onAccountChange: (value: string) => void;
  onProviderChange: (value: string) => void;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onRunReport: () => void;
  onExportCsv: () => void;
}

const PaymentReportSection: React.FC<PaymentReportSectionProps> = ({
  reportAccount,
  reportProvider,
  reportFrom,
  reportTo,
  aggregates,
  reportError,
  isLoading,
  onAccountChange,
  onProviderChange,
  onFromChange,
  onToChange,
  onRunReport,
  onExportCsv,
}) => {
  const providerOption =
    PAYMENT_PROVIDER_OPTIONS.find((o) => o.value === reportProvider) ?? PAYMENT_PROVIDER_OPTIONS[0];

  const totalCharged =
    (aggregates?.completedTotalChargedCents ??
      (aggregates?.completedAmountCents || 0) + (aggregates?.completedSurchargeCents || 0)) / 100;

  const providerColumns = useMemo(
    () => [
      providerColumnHelper.accessor("provider", { header: "Provider" }),
      providerColumnHelper.accessor("count", { header: "Count" }),
      providerColumnHelper.accessor("amountCents", {
        header: "Base",
        cell: (info) => <Amount amount={(info.getValue() || 0) / 100} />,
      }),
      providerColumnHelper.accessor("surchargeCents", {
        header: "Surcharge",
        cell: (info) => <Amount amount={(info.getValue() || 0) / 100} />,
      }),
      providerColumnHelper.accessor("totalChargedCents", {
        header: "Total",
        cell: (info) => {
          const row = info.row.original;
          const total =
            (row.totalChargedCents ?? (row.amountCents || 0) + (row.surchargeCents || 0)) / 100;
          return <Amount amount={total} />;
        },
      }),
    ],
    []
  );

  return (
    <div className="xgs-payment-admin__report">
      <div className="xgs-payment-admin__filters">
        <LabeledTextInput
          label="Account"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          value={reportAccount}
          onChange={(e) => onAccountChange(e.target.value)}
          placeholder="Account #"
        />
        <LabeledSelectInput
          label="Provider"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          options={PAYMENT_PROVIDER_OPTIONS}
          value={providerOption}
          onValueChange={(option) => onProviderChange(option?.value ?? "")}
        />
        <LabeledTextInput
          label="From"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          type="date"
          value={reportFrom}
          onChange={(e) => onFromChange(e.target.value)}
        />
        <LabeledTextInput
          label="To"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          type="date"
          value={reportTo}
          onChange={(e) => onToChange(e.target.value)}
        />
        <Button theme={ButtonThemes.blue} onClick={onRunReport}>
          Run report
        </Button>
        <Button theme={ButtonThemes.gray} onClick={onExportCsv} disabled={!aggregates}>
          Export CSV
        </Button>
      </div>
      {reportError && <XGSErrorMessage>{reportError}</XGSErrorMessage>}
      {aggregates && (
        <div className="xgs-payment-admin__card">
          <div className="xgs-payment-admin__summary">
            <PaymentSummarySection title="Volume">
              <MetricSummaryTile
                iconPreset="fileBlue"
                colorVariant="blue"
                isLoading={isLoading}
                title="Total payments"
                value={aggregates.totalPayments}
                legendDescription="Total payment records matching the selected filters."
              />
              <MetricSummaryTile
                iconPreset="fileGreenCheck"
                colorVariant="green"
                isLoading={isLoading}
                title="Completed"
                value={aggregates.completedCount}
                legendDescription="Payments that completed successfully."
              />
              <MetricSummaryTile
                iconPreset="filePurplePending"
                colorVariant="purple"
                isLoading={isLoading}
                title="Pending"
                value={aggregates.pendingCount}
                legendDescription="Payments still awaiting completion."
              />
              <MetricSummaryTile
                iconPreset="clockYellow"
                colorVariant="yellow"
                isLoading={isLoading}
                title="Expired"
                value={aggregates.expiredCount}
                legendDescription="Payments that expired before completion."
              />
            </PaymentSummarySection>

            <PaymentSummarySection title="Success">
              <MetricSummaryTile
                iconPreset="checkBlueCircle"
                colorVariant="green"
                isLoading={isLoading}
                title="Success rate"
                value={formatPct(aggregates.successRate)}
                legendDescription="Completed payments as a percentage of terminal outcomes."
              />
              <MetricSummaryTile
                iconPreset="exclamationRed"
                colorVariant="red"
                isLoading={isLoading}
                title="Fail rate"
                value={formatPct(aggregates.failRate)}
                legendDescription="Failed payments as a percentage of terminal outcomes."
              />
              <MetricSummaryTile
                iconPreset="exclamationRed"
                colorVariant="red"
                isLoading={isLoading}
                title="Failed"
                value={aggregates.failedCount}
                legendDescription="Payments that failed during processing."
              />
              <MetricSummaryTile
                iconPreset="clockYellow"
                colorVariant="yellow"
                isLoading={isLoading}
                title="Cancelled"
                value={aggregates.cancelledCount}
                legendDescription="Payments cancelled before completion."
              />
            </PaymentSummarySection>

            <PaymentSummarySection title="Amounts">
              <MetricSummaryTile
                iconPreset="fileBlue"
                colorVariant="blue"
                isLoading={isLoading}
                title="Base received"
                value={<Amount amount={(aggregates.completedAmountCents || 0) / 100} />}
                legendDescription="Sum of invoice base amounts for completed payments."
              />
              <MetricSummaryTile
                iconPreset="clockYellow"
                colorVariant="yellow"
                isLoading={isLoading}
                title="Surcharge"
                value={<Amount amount={(aggregates.completedSurchargeCents || 0) / 100} />}
                legendDescription="Sum of surcharge amounts for completed payments."
              />
              <MetricSummaryTile
                iconPreset="fileGreenCheck"
                colorVariant="green"
                isLoading={isLoading}
                title="Total charged"
                value={
                  <span className="xgs-payment-admin__highlight-value">
                    <Amount amount={totalCharged} />
                  </span>
                }
                legendDescription="Base received plus surcharge for completed payments."
              />
            </PaymentSummarySection>
          </div>

          {aggregates.completedAmountByProvider?.length ? (
            <div className="xgs-payment-admin__provider-table">
              <h4 className="xgs-payment-admin__card__title">Completed by provider</h4>
              <Table
                columns={providerColumns}
                data={aggregates.completedAmountByProvider}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default PaymentReportSection;
